import { motion } from "framer-motion";
import { ClipboardList, MessageCircle, ListChecks, BarChart3, Smile, CalendarCheck, Users, Eye } from "lucide-react";

const steps = [
  {
    n: 1,
    icon: ClipboardList,
    title: "Créez un plan d'étude",
    body: "Définissez un plan simple en 2 minutes : jours, durée, plage horaire.",
  },
  {
    n: 2,
    icon: MessageCircle,
    title: "Rappels automatiques",
    body: "Votre enfant reçoit un rappel au bon moment, sans friction.",
  },
  {
    n: 3,
    icon: ListChecks,
    title: "Suivi simple",
    body: "Il confirme ce qu'il a fait : oui/non, durée, matière, court bilan.",
  },
  {
    n: 4,
    icon: BarChart3,
    title: "Rapport parent",
    body: "Vous recevez un rapport clair sur les progrès chaque semaine.",
  },
];

const outcomes = [
  { icon: Smile, label: "Moins de conflits" },
  { icon: CalendarCheck, label: "Plus de régularité" },
  { icon: Users, label: "Plus d'autonomie" },
  { icon: Eye, label: "Une visibilité claire" },
];

const HowItWorks = () => {
  return (
    <section
      id="fonctionnement"
      data-testid="how-it-works-section"
      className="relative py-24 sm:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-xs uppercase font-bold tracking-[0.2em] text-[#189346]">
            Fonctionnement
          </div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#122118] leading-[1.1]">
            Comment ça fonctionne
          </h2>
          <p className="mt-4 text-lg text-[#4a5f53] leading-relaxed">
            Un système simple en 4 étapes qui crée de vraies habitudes d'étude
            — sans surveillance lourde, sans application à installer.
          </p>
        </div>

        {/* Steps */}
        <div className="relative mt-14 grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-4">
          {/* dotted connector (desktop) */}
          <div
            aria-hidden
            className="hidden md:block absolute top-9 left-[12.5%] right-[12.5%] h-0.5 border-t-2 border-dashed border-[#25D366]/30"
          />
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: i * 0.08, duration: 0.45 }}
              className="relative bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow text-center"
              data-testid={`how-step-${s.n}`}
            >
              <div
                className="relative z-10 mx-auto w-16 h-16 rounded-full flex items-center justify-center border-4 border-white shadow-sm"
                style={{ background: "#E9FBF0" }}
              >
                <s.icon className="w-6 h-6 text-[#189346]" strokeWidth={2.2} />
              </div>
              <div className="mt-3 text-xs font-bold tracking-wider text-[#189346]">
                ÉTAPE {s.n}
              </div>
              <h3 className="mt-1.5 font-display font-semibold text-lg text-[#122118]">
                {s.title}
              </h3>
              <p className="mt-2 text-sm text-[#4a5f53] leading-relaxed">
                {s.body}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Outcomes */}
        <div className="mt-12 rounded-3xl bg-[#E9FBF0]/70 p-6 sm:p-8 border border-[#25D366]/15">
          <div className="text-sm font-semibold text-[#189346] mb-4">
            En quelques semaines :
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {outcomes.map((o) => (
              <div
                key={o.label}
                className="flex items-center gap-3"
                data-testid={`outcome-${o.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <span
                  className="w-9 h-9 rounded-xl flex items-center justify-center"
                  style={{ background: "#fff", color: "#189346" }}
                >
                  <o.icon className="w-4 h-4" />
                </span>
                <span className="text-sm font-medium text-[#122118]">
                  {o.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
